import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { googleLogin, postJson, useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

const GoogleBtn = () => {
  const { t } = useLang();
  return (
    <button type="button" onClick={googleLogin} className="btn-ghost w-full justify-center" data-testid="google-login-btn">
      <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
      {t("auth.google")}
    </button>
  );
};

const Field = ({ label, ...p }) => (
  <label className="block text-left">
    <span className="mono mb-2 block text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">{label}</span>
    <input className="support-input" {...p} />
  </label>
);

export default function Login() {
  const { t } = useLang();
  const { member, setMember } = useAuth();
  const [params] = useSearchParams();
  const [mode, setMode] = useState("login");
  const [v, setV] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(params.get("error") ? t("auth.googleErr") : "");
  const next = params.get("next") || "/perfil";
  if (member) return <Navigate to={member.onboarded === false ? `/perfil/cuestionario?next=${encodeURIComponent(next)}` : next} replace />;
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = mode === "login" ? { email: v.email, password: v.password } : v;
      const m = await postJson(`/members/${mode === "login" ? "login" : "register"}`, body);
      setMember(m);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="px-5 pb-28 pt-32 md:px-10" data-testid="login-page">
      <div className="mx-auto max-w-md">
        <p className="mono text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]">{t("auth.kicker")}</p>
        <h1 className="font-display mt-4 text-5xl leading-[0.95] sm:text-6xl">{mode === "login" ? t("auth.loginTitle") : t("auth.registerTitle")}</h1>
        <p className="mt-4 text-sm text-[var(--muted)]">{t("auth.intro")}</p>
        <div className="mt-8 inline-flex rounded-full border border-white/15 bg-white/5 p-1" data-testid="auth-mode-toggle">
          {["login", "register"].map((k) => (
            <button key={k} type="button" onClick={() => { setMode(k); setError(""); }} className={`col-mode ${mode === k ? "col-mode-on" : ""}`} data-testid={`auth-mode-${k}`}>{t(`auth.${k}`)}</button>
          ))}
        </div>
        <div className="mt-6"><GoogleBtn /></div>
        <p className="mono my-5 text-center text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">{t("auth.or")}</p>
        <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
          {mode === "register" && <Field label={t("form.name")} required minLength={2} maxLength={60} value={v.name} onChange={set("name")} data-testid="auth-name-input" />}
          <Field label={t("form.email")} type="email" required value={v.email} onChange={set("email")} autoComplete="email" data-testid="auth-email-input" />
          <Field label={t("auth.password")} type="password" required minLength={mode === "register" ? 8 : 1} value={v.password} onChange={set("password")} autoComplete={mode === "login" ? "current-password" : "new-password"} data-testid="auth-password-input" />
          {mode === "register" && <p className="text-xs text-[var(--muted)]">{t("auth.pwHint")}</p>}
          {error && <p className="text-sm text-[#ff8a7a]" data-testid="auth-error">{error}</p>}
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="auth-submit-btn">
            {busy && <Loader2 size={16} className="animate-spin" />} {mode === "login" ? t("auth.login") : t("auth.create")}
          </button>
        </form>
        {mode === "login" && <Link to="/restablecer" className="mt-5 inline-block text-sm text-[var(--green)]" data-testid="forgot-password-link">{t("auth.forgot")}</Link>}
      </div>
    </main>
  );
}
